// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the bottom sheets.
//
// All over the same scrim: change the status, change the priority, the assignees,
// edit, and create. Each is built fresh when it opens and thrown away when it closes, so
// there is no stale draft to reconcile.
//
// As on the detail screen, the text a user is typing lives in the field until it
// is submitted — the confirm button is enabled in place rather than by writing a
// draft into the store and re-rendering the sheet under the caret.
// ─────────────────────────────────────────────────────────────────────────────

import {
    assigneeChoices,
    movesOf,
    personMatchesQuery,
    transitionLabel,
    transitionNoteHint,
    namesWithSelf,
} from "home/app/mobile/todos";
import { type LifecycleTransition, type Priority, type Todo } from "home/app/mobile/types";
import { TODO_PRIORITIES, priorityName, priorityOf } from "home/app/mobile/priorities";
import { type MobileState } from "home/app/mobile/store";
import { APPEARANCES, appearanceLabel } from "home/app/mobile/appearance";
import { LANGUAGE_CHOICES, languageChoiceLabel, t } from "home/app/mobile/i18n";
import { relationListName, relationName } from "home/app/mobile/relationNames";
import { activeTenant, tenantRowSubtitle } from "home/app/mobile/tenants";

import { bind, el } from "home/ui/mobile/dom";
import { dueFacts } from "home/app/mobile/due";
import { openDatePicker } from "home/ui/mobile/ui_mobile_datepicker";
import { chevronRight, icon } from "home/ui/mobile/icons";
import { priorityChip, type TodoActions } from "home/ui/mobile/ui_mobile_todos";
import { type DrawnState, stateDot, transitionTarget } from "home/ui/mobile/state";

/**
 * The scrim + panel.
 *
 * A click on the scrim itself closes; a click inside the panel does not. Escape
 * closes too — this app runs on phones but is a web page, and a keyboard user
 * must not be trapped in a sheet.
 */
function sheet(
    title: string,
    children: HTMLElement[],
    onClose: () => void,
    high = false,
    /**
     * What closing MEANS, for the close cross's accessible name: "Cancel" where closing discards a
     * draft; "Done" where every change was already saved as it was made (the assignees, the
     * account), so there is nothing left to cancel.
     */
    closeLabel: "Cancel" | "Done" = "Cancel",
): HTMLElement {
    // A CROSS, not a word (2026-10-18): one shape for "close this card" on every sheet, and no word
    // taking a third of the title row. Its name still says what closing does.
    const cancel = el(
        "button",
        {
            class: "pqm-appbar-icon pqm-sheet-close",
            type: "button",
            "aria-label": closeLabel === "Done" ? t("mobileSheetDone", "Done") : t("mobileSheetCancel", "Cancel"),
        },
        [icon("x", 18)],
    );
    bind(cancel, "click", onClose);

    const panel = el("div", { class: "pqm-sheet", role: "dialog", "aria-modal": "true", "aria-label": title }, [
        el("div", { class: "pqm-sheet-head" }, [el("div", { class: "pqm-h pqm-sheet-title", text: title }), cancel]),
        ...children,
    ]);

    const scrim = el("div", { class: `pqm-scrim${high ? " pqm-scrim-high" : ""}` }, [panel]);
    bind(scrim, "click", (event) => {
        if (event.target === scrim) onClose();
    });
    bind(scrim, "keydown", (event) => {
        if ((event as KeyboardEvent).key === "Escape") onClose();
    });
    // Focusable so Escape reaches it even before anything inside is focused. Focusing it is the
    // SHELL's job, and only when the sheet opens — see `steadyOverlays()` in ui_mobile_shell.ts:
    // this function runs on every repaint, and focusing here stole focus on each one.
    scrim.tabIndex = -1;

    return scrim;
}

/** A state as circle AND word — the sheet is where somebody checks what they are about to do. */
function namedState(state: DrawnState): HTMLElement {
    return el("span", { class: "pqm-named-state" }, [stateDot(state, "sm", true), el("span", { text: state.status })]);
}

/**
 * A list of options where at most ONE is picked, and nothing is picked until somebody taps.
 *
 * The pick lives in the sheet — `aria-pressed` on the rows, and `onPick` telling the sheet — not in
 * the store: writing it there would repaint the sheet and throw away a comment being typed.
 */
function pickList<T>(
    options: Array<{ value: T; content: Array<Node>; label: string }>,
    picked: T | null,
    onPick: (value: T) => void,
): HTMLElement {
    const rows = options.map((option) => {
        const row = el(
            "button",
            {
                class: "pqm-pick pqm-pick-row pqm-pick-option",
                type: "button",
                "aria-pressed": option.value === picked ? "true" : "false",
                "aria-label": option.label,
            },
            option.content,
        );
        bind(row, "click", () => {
            for (const other of rows) other.setAttribute("aria-pressed", other === row ? "true" : "false");
            onPick(option.value);
        });
        return row;
    });

    return el("div", { class: "pqm-picks-col", role: "group" }, rows);
}

/** "Currently: …" above a sheet's options, so the choice is made knowing where it starts. */
function currently(value: Node): HTMLElement {
    return el("div", { class: "pqm-currently" }, [
        el("span", { class: "pqm-currently-label", text: t("mobileSheetCurrently", "Currently") }),
        value,
    ]);
}

/**
 * Change the status: choose the target, then confirm.
 *
 * ⚠ TWO STEPS, ON PURPOSE. The detail screen used to show every target as a button, and a stray tap
 * on a phone was a move. Here nothing is preselected — the Confirm button says "Choose a status"
 * and is disabled until a target is picked — and only Confirm fires. A move the tenant refuses
 * keeps the sheet open with the pick and the comment intact (both ride in the draft).
 *
 * The targets are the MOVES the API offers this person, each as the state it leads to — never a
 * transition's own name (see transitionLabel()). The self-transition is not here: it is the
 * comment field on the detail screen.
 */
function statusSheet(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    const draft = state.transitionDraft!;
    const moves = movesOf(todo);
    let picked: LifecycleTransition | null = draft.transition
        ? (moves.find((m) => m.id === draft.transition!.id) ?? null)
        : null;

    const note = el("textarea", {
        class: "input",
        style: "min-height:88px",
        placeholder: t("mobileSheetCommentPlaceholder", "What should the others know?"),
        "aria-label": t("mobileSheetComment", "Comment"),
    });
    // The draft carries the note across a re-render, so a refused move does not also throw away the
    // reason the user just typed.
    note.value = draft.note;

    const confirm = el("button", {
        class: "btn btn-primary btn-block pqm-move pqm-confirm",
        style: "min-height:48px",
        type: "button",
    });
    // The confirm button says what it does once there is a target — "Set to <state>", with the
    // state's circle as the list will paint it — and what is missing until then. A note is always optional.
    const paintConfirm = (): void => {
        confirm.disabled = draft.busy || picked === null;
        if (picked) {
            confirm.replaceChildren(
                stateDot(transitionTarget(picked), "sm", true),
                el("span", { text: t("mobileSheetSetTo", "Set to {state}", { state: transitionLabel(picked) }) }),
            );
        } else {
            confirm.replaceChildren(el("span", { text: t("mobileSheetChooseStatus", "Choose a status") }));
        }
    };
    paintConfirm();

    bind(confirm, "click", () => {
        if (!picked || confirm.disabled) return;
        confirm.disabled = true;
        actions.confirmTransition(picked, transitionLabel(picked), note.value);
    });

    const options = pickList(
        moves.map((move) => ({
            value: move,
            label: transitionLabel(move),
            content: [
                stateDot(transitionTarget(move), "sm", true),
                el("span", { class: "pqm-move-label", text: transitionLabel(move) }),
            ],
        })),
        picked,
        (move) => {
            picked = move;
            paintConfirm();
        },
    );

    const children: HTMLElement[] = [
        currently(namedState(todo)),
        options,
        el("div", { class: "field" }, [
            el("label", { text: t("mobileSheetComment", "Comment") }),
            note,
            el("div", { class: "pqm-hint", style: "padding-top:6px", text: transitionNoteHint() }),
        ]),
    ];

    if (draft.error) {
        children.push(
            el("div", { class: "pqm-note" }, [
                icon("warningCircle", 15, "var(--color-accent-650)"),
                el("div", { class: "pqm-note-text", text: draft.error }),
            ]),
        );
    }

    children.push(confirm);

    return sheet(t("mobileViewChangeStatus", "Change status"), children, actions.closeSheet, true);
}

/**
 * Change the priority: choose, then confirm. Owner only — the server refuses anyone else.
 *
 * Nothing is preselected; the current value is stated above the options instead, and picking it
 * leaves Confirm disabled, since sending it would change nothing. The pick lives in the STORE
 * (`priorityPick`, reset when the sheet opens), so the repaints a change causes keep it: the sheet
 * closes when the server confirms, and stays open with the pick when it refuses — see setPriority().
 */
function prioritySheet(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    const current = priorityOf(todo.priority)?.id ?? "";
    const picked = state.priorityPick;

    const confirm = el("button", {
        class: "btn btn-primary btn-block pqm-confirm",
        style: "min-height:48px",
        type: "button",
    });
    const paintConfirm = (): void => {
        confirm.disabled = state.priorityBusy || picked === null || picked === current;
        confirm.textContent =
            picked === null || picked === current
                ? t("mobileSheetChoosePriority", "Choose a priority")
                : picked === ""
                  ? t("mobileSheetRemovePriority", "Remove priority")
                  : t("mobileSheetSetPriority", "Set to {priority}", { priority: priorityName(picked) });
    };
    paintConfirm();

    bind(confirm, "click", () => {
        if (picked === null || confirm.disabled) return;
        confirm.disabled = true;
        actions.setPriority(picked);
    });

    const dot = (color: string) =>
        el("span", { class: "pqm-prio-dot", style: `background:${color}`, "aria-hidden": "true" });
    const options = pickList<Priority | "">(
        [
            {
                value: "",
                label: t("mobileSheetNoPriority", "None"),
                content: [el("span", { text: t("mobileSheetNoPriority", "None") })],
            },
            ...TODO_PRIORITIES.map((p) => ({
                value: p.id as Priority | "",
                label: priorityName(p.id),
                content: [dot(p.color), el("span", { text: priorityName(p.id) })],
            })),
        ],
        picked,
        (value) => actions.pickPriority(value),
    );

    return sheet(
        t("mobileViewChangePriority", "Change priority"),
        [
            currently(
                priorityChip(todo.priority) ??
                    el("span", { class: "pqm-change-value", text: t("mobileSheetNoPriority", "None") }),
            ),
            options,
            confirm,
        ],
        actions.closeSheet,
    );
}

/**
 * The assignees: who has this TODO, and who could be given it too.
 *
 * ⚠ ONE PERSON PER TAP. A TODO may have several assignees, so there is no "reassign" — each row adds
 * or removes exactly that person and leaves everybody else as they were. Each tap is saved at once
 * and the sheet stays open, because changing two people is an ordinary thing to want.
 *
 * While one change is in flight every button is disabled: each answers with the whole TODO, and a
 * second tap before the first returns would repaint over it.
 */
/**
 * What is typed into the assignees sheet's search, across repaints.
 *
 * ⚠ NOT IN THE STORE. Every store update repaints the whole app, so writing each keystroke there
 * would rebuild the field under the caret. The rows are filtered IN PLACE instead, and this is only
 * read back when something ELSE repaints the sheet — the "…" on a button, the answer, the toast after
 * an add. Without it one tap cleared the search the person had just used to find somebody. Reset
 * whenever the sheet is not open, so it opens empty the next time.
 */
let assigneeQuery = "";

/** The title and description being typed in the details sheet — see detailsSheet(). */
let detailsDraft: { id: string; title: string; description: string } | null = null;

function assigneesSheet(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    const { assigned, others } = assigneeChoices(todo, state.createOptions?.people ?? []);
    const busy = state.assigneeBusy;

    const row = (person: { id: string; name: string }, verb: "Add" | "Remove", onClick: () => void) => {
        const name = person.name || t("mobileSheetUnnamedResource", "Unnamed resource");
        const label = verb === "Add" ? t("mobileSheetAdd", "Add") : t("mobileSheetRemove", "Remove");
        const button = el("button", {
            class: `btn ${verb === "Add" ? "btn-secondary" : "btn-ghost"} pqm-assignee-action`,
            type: "button",
            text: busy === person.id ? "…" : label,
            "aria-label":
                verb === "Add"
                    ? t("mobileSheetAddPerson", "Add {name}", { name })
                    : t("mobileSheetRemovePerson", "Remove {name}", { name }),
        });
        button.disabled = busy !== null;
        bind(button, "click", onClick);

        return el("div", { class: "pqm-assignee-row" }, [
            el("div", { class: "pqm-avatar pqm-avatar-md", text: initials(name), "aria-hidden": "true" }),
            el("div", { class: "pqm-assignee-name", text: name }),
            button,
        ]);
    };

    const children: HTMLElement[] = [
        el("div", { class: "pqm-kicker", text: t("mobileSheetAssigned", "Assigned") }),
        assigned.length
            ? el(
                  "div",
                  { class: "pqm-picks-col" },
                  assigned.map((a) => row(a, "Remove", () => actions.unassign(a.id))),
              )
            : el("div", { class: "pqm-empty", text: t("mobileSheetNobodyAssigned", "Nobody is assigned.") }),
        el("div", { class: "pqm-kicker", text: t("mobileSheetAdd", "Add") }),
        others.length
            ? addList(others.map((p) => ({ name: p.name, row: row(p, "Add", () => actions.assign(p.id)) })))
            : el("div", {
                  class: "pqm-empty",
                  text: state.createOptions
                      ? t("mobileSheetNobodyElse", "Nobody else can be assigned.")
                      : t("mobileSheetLoadingPeople", "Loading who can be assigned…"),
              }),
        el("div", {
            class: "pqm-hint",
            text: t(
                "mobileSheetAssigneesHint",
                "Each change is saved straight away. The status and the comment history stay as they are.",
            ),
        }),
    ];

    return sheet(
        relationName("assignee", t("mobileSheetAssignees", "Assignees")),
        children,
        actions.closeSheet,
        true,
        "Done",
    );
}

/**
 * The "Add" list with its search field above it. Only this list is searched: "Assigned" is a
 * handful of people, and hiding one of them behind a query would read as having removed them.
 *
 * ⚠ The field is rebuilt on every repaint like everything else, so a field that HAD focus gets it
 * back — otherwise the toast after an add, repainting a second later, would close the phone's
 * keyboard in the middle of the next word. It is deliberately not focused on OPENING: the keyboard
 * would cover the list the person came to read.
 */
function addList(entries: { name: string; row: HTMLElement }[]): HTMLElement {
    const hadFocus = document.activeElement?.classList.contains("pqm-assignee-search") ?? false;
    const label = t("mobileSheetSearchPeople", "Search by name");

    const search = el("input", {
        class: "input pqm-assignee-search",
        type: "search",
        placeholder: label,
        "aria-label": label,
        autocomplete: "off",
        enterkeyhint: "search",
    });
    search.value = assigneeQuery;

    const none = el("div", { class: "pqm-empty" });
    const list = el(
        "div",
        { class: "pqm-picks-col" },
        entries.map((e) => e.row),
    );

    const apply = (): void => {
        let shown = 0;
        for (const entry of entries) {
            const match = personMatchesQuery(entry.name, assigneeQuery);
            entry.row.hidden = !match;
            if (match) shown++;
        }
        none.hidden = shown > 0;
        none.textContent = t("mobileSheetNoPersonMatches", "Nobody matches “{query}”.", {
            query: assigneeQuery.trim(),
        });
    };
    bind(search, "input", () => {
        assigneeQuery = search.value;
        apply();
    });
    apply();

    if (hadFocus) {
        window.setTimeout(() => {
            search.focus();
            search.setSelectionRange(search.value.length, search.value.length);
        }, 0);
    }

    return el("div", { class: "pqm-assignee-add" }, [search, list, none]);
}

function initials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0]!)
        .join("")
        .slice(0, 2)
        .toUpperCase();
}

/**
 * A form field whose options fold away under a header: its label, the CURRENT choice, and a chevron.
 *
 * ⚠ STARTS FOLDED. The project and people lists can run to dozens, and open they pushed the due
 * date, the priority and the Create button off a phone's screen. The header states the choice, so
 * a folded field still says what will be sent — nothing is hidden that the form is about to use.
 *
 * Opened, the list has a SEARCH field above it, focused, filtering the options by their
 * `data-name` with the app's one matching rule (`personMatchesQuery()`: every word, any order, case
 * and accents ignored). An option marked `data-always` — "No project", "Nobody yet" — is never
 * filtered out: it is the way back, and must not be something a query can hide.
 *
 * Returns the field, `close()` (a pick calls it: choosing is the end of the job, and it clears the
 * search so the list opens whole next time) and `show()` for the header's value.
 */
function collapsibleField(
    label: string,
    options: HTMLElement,
    search: { placeholder: string; noMatch: (query: string) => string },
): { field: HTMLElement; close: () => void; show: (choice: string) => void } {
    const value = el("span", { class: "pqm-fold-value" });
    const head = el(
        "button",
        { class: "pqm-fold-head pqm-group-collapsed", type: "button", "aria-expanded": "false" },
        [
            el("span", { class: "pqm-fold-label", text: label }),
            value,
            el("span", { class: "pqm-group-chevron" }, [chevronRight()]),
        ],
    );

    const input = el("input", {
        class: "input pqm-fold-search",
        type: "search",
        placeholder: search.placeholder,
        "aria-label": search.placeholder,
        autocomplete: "off",
        enterkeyhint: "search",
    });
    const none = el("div", { class: "pqm-empty pqm-fold-none" });
    const body = el("div", { class: "pqm-fold-body" }, [input, options, none]);
    body.hidden = true;

    const filter = (): void => {
        let shown = 0;
        for (const option of Array.from(options.querySelectorAll<HTMLElement>("[data-name]"))) {
            const match =
                option.hasAttribute("data-always") || personMatchesQuery(option.dataset.name ?? "", input.value);
            option.hidden = !match;
            if (match && !option.hasAttribute("data-always")) shown++;
        }
        const query = input.value.trim();
        none.hidden = query === "" || shown > 0;
        none.textContent = query ? search.noMatch(query) : "";
    };
    bind(input, "input", filter);

    const setOpen = (open: boolean): void => {
        body.hidden = !open;
        head.setAttribute("aria-expanded", open ? "true" : "false");
        head.classList.toggle("pqm-group-collapsed", !open);
        if (open) input.focus();
    };
    bind(head, "click", () => setOpen(body.hidden));
    filter();

    return {
        field: el("div", { class: "field pqm-fold" }, [head, body]),
        close: () => {
            input.value = "";
            filter();
            setOpen(false);
        },
        show: (choice: string) => {
            value.textContent = choice;
        },
    };
}

/** Create a TODO. It starts in the lifecycle's own initial state, not one named here. */
function newTodoSheet(state: MobileState, actions: TodoActions): HTMLElement {
    const options = state.createOptions;
    const projects = options?.projects ?? [];
    const people = options?.people ?? [];

    // ⚠ NOTHING IS PRESELECTED — no project, no assignee, like the due date and the priority below.
    // Taking the first of each filed every new TODO under whichever project and person the list
    // happened to start with, whether or not anybody meant to. Both are optional: a TODO may belong to
    // no project and be assigned to nobody yet — PQFORCE accepts both, so only the title is required.
    const draft = {
        // Empty — unless the sheet was opened from a project group's +, which names its project.
        projectId: state.newTodoProjectId ?? "",
        assigneeId: "",
        // ⚠ A DATE, and empty by default. The four labels this replaced —
        // Today / Tomorrow / This week / Later — could not express any other day,
        // and "This week" as a default dated every new TODO whether or not
        // anybody meant to. Nothing is due until somebody says so.
        dueISO: "",
        // ⚠ NONE by default, like the due date: most TODOs carry no priority, and a default would
        // set one on every TODO whether or not anybody meant to.
        priority: "" as Priority | "",
    };

    const title = el("input", {
        class: "input",
        style: "min-height:46px",
        type: "text",
        placeholder: t("mobileSheetTitlePlaceholder", "e.g. Confirm night tariff rounding"),
        "aria-label": t("mobileSheetWhatNeedsDoing", "What needs doing?"),
    });

    /** A group of pick buttons where exactly one is selected. */
    const pickGroup = <T extends { id: string; name: string }>(
        items: T[],
        current: () => string,
        set: (id: string) => void,
        cls: string,
        render?: (item: T) => Array<Node>,
    ): HTMLElement[] =>
        items.map((item) => {
            const button = el(
                "button",
                {
                    class: `pqm-pick ${cls}`,
                    type: "button",
                    "aria-pressed": current() === item.id ? "true" : "false",
                    // What a folding field's search matches, and the "none" entry it never hides.
                    "data-name": item.name,
                    "data-always": item.id === "" ? true : null,
                },
                render ? render(item) : [document.createTextNode(item.name)],
            );
            bind(button, "click", () => {
                set(item.id);
                const parent = button.parentElement;
                if (parent) {
                    for (const sibling of Array.from(parent.children)) {
                        sibling.setAttribute("aria-pressed", sibling === button ? "true" : "false");
                    }
                }
            });
            return button;
        });

    // "No project" / "Nobody yet" first: the start, and the only way back once something is picked.
    const projectOptions = [{ id: "", name: t("mobileSheetNoProject", "No project") }, ...projects];
    const assigneeOptions = [{ id: "", name: t("mobileSheetNoAssignee", "Nobody yet") }, ...people];
    const nameOf = (options: Array<{ id: string; name: string }>, id: string): string =>
        options.find((option) => option.id === id)?.name ?? "";

    const projectRow = el(
        "div",
        { class: "pqm-picks-col" },
        pickGroup(
            projectOptions,
            () => draft.projectId,
            (id) => {
                draft.projectId = id;
                projectField.show(nameOf(projectOptions, id));
                projectField.close();
            },
            "",
        ),
    );

    const assigneeRow = el(
        "div",
        { class: "pqm-picks-row" },
        pickGroup(
            assigneeOptions,
            () => draft.assigneeId,
            (id) => {
                draft.assigneeId = id;
                assigneeField.show(nameOf(assigneeOptions, id));
                assigneeField.close();
            },
            "pqm-pick-person",
            (person) => [
                // No avatar for "Nobody yet": initials of that phrase would read as a person.
                ...(person.id
                    ? [
                          el("span", {
                              class: "pqm-avatar pqm-avatar-sm",
                              text: initials(person.name),
                              "aria-hidden": "true",
                          }),
                      ]
                    : []),
                document.createTextNode(person.name),
            ],
        ),
    );

    // Folded under their current choice — see collapsibleField(). Built after the rows, which they
    // wrap; the rows' pick handlers only reach them on a tap, by which time both exist.
    const projectField = collapsibleField(t("mobileSheetProject", "Project"), projectRow, {
        placeholder: t("mobileSheetSearchProjects", "Search projects"),
        noMatch: (query) => t("mobileSheetNoProjectMatches", "No project matches “{query}”.", { query }),
    });
    projectField.show(nameOf(projectOptions, draft.projectId));
    const assigneeField = collapsibleField(
        relationName("assignee", t("mobileSheetAssignee", "Assignee")),
        assigneeRow,
        {
            placeholder: t("mobileSheetSearchPeople", "Search by name"),
            noMatch: (query) => t("mobileSheetNoPersonMatches", "Nobody matches “{query}”.", { query }),
        },
    );
    assigneeField.show(nameOf(assigneeOptions, draft.assigneeId));

    /**
     * One button that opens the picker, rather than a row of chips.
     *
     * ⚠ The picker MOUNTS ITSELF over this sheet and changes no state, which is what lets the
     * half-written todo survive it — see `openDatePicker()`. Routing it through the store would
     * rebuild this sheet and empty the title field.
     */
    const dueButton = el("button", { class: "pqm-pick pqm-pick-chip pqm-due-button", type: "button" });
    const paintDue = () => {
        dueButton.textContent = draft.dueISO
            ? dueFacts(draft.dueISO, Date.now()).label
            : t("mobileSheetNoDueDate", "No due date");
    };
    bind(dueButton, "click", () => {
        const host = dueButton.closest(".pqm-scrim")?.parentElement ?? document.body;
        openDatePicker(host as HTMLElement, {
            selected: draft.dueISO,
            now: Date.now(),
            onSave: (iso) => {
                draft.dueISO = iso;
                paintDue();
            },
        });
    });
    paintDue();

    const dueRow = el("div", { class: "pqm-picks-tight" }, [dueButton]);

    const priorityRow = el(
        "div",
        { class: "pqm-picks-tight" },
        // The PQFORCE client's own three, in its order, after "None" — which is a real answer and
        // the only way back once a chip is picked.
        pickGroup(
            [
                { id: "", name: t("mobileSheetNoPriority", "None"), color: "" },
                ...TODO_PRIORITIES.map((p) => ({ id: p.id, name: priorityName(p.id), color: p.color })),
            ],
            () => draft.priority,
            (id) => {
                draft.priority = (priorityOf(id)?.id ?? "") as Priority | "";
            },
            "pqm-pick-chip",
            (option) => [
                ...(option.color
                    ? [
                          el("span", {
                              class: "pqm-prio-dot",
                              style: `background:${option.color}`,
                              "aria-hidden": "true",
                          }),
                      ]
                    : []),
                document.createTextNode(option.name),
            ],
        ),
    );

    const create = el("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:48px",
        type: "button",
        text: t("mobileSheetCreateTodo", "Create TODO"),
    });
    /** What is still missing before Create can work — said, never left to a greyed-out button. */
    const missing = el("div", { class: "pqm-hint pqm-sheet-missing", role: "status" });

    /**
     * Create waits for a TITLE, the one thing a TODO cannot be without — and says so rather than
     * greying out silently. (It used to stay disabled for good after a tap with no title.)
     */
    const refresh = (): void => {
        const noTitle = title.value.trim() === "";
        create.disabled = noTitle;
        missing.textContent = noTitle ? t("mobileShellTitleMissing", "Give the TODO a title first.") : "";
        missing.hidden = noTitle === false;
    };
    bind(title, "input", refresh);
    refresh();

    bind(create, "click", () => {
        if (create.disabled) return;
        create.disabled = true;
        actions.createTodo({
            title: title.value.trim(),
            projectId: draft.projectId,
            assigneeId: draft.assigneeId,
            dueISO: draft.dueISO,
            priority: draft.priority,
        });
    });

    return sheet(
        t("mobileSheetNewTodo", "New TODO"),
        [
            el("div", { class: "field" }, [
                el("label", { text: t("mobileSheetWhatNeedsDoing", "What needs doing?") }),
                title,
            ]),
            projectField.field,
            assigneeField.field,
            el("div", { style: "display:flex;gap:14px;flex-wrap:wrap" }, [
                el("div", { class: "field", style: "flex:1" }, [
                    el("label", { text: t("mobileSheetDue", "Due") }),
                    dueRow,
                ]),
                el("div", { class: "field", style: "flex:none" }, [
                    el("label", { text: t("mobileSheetPriority", "Priority") }),
                    priorityRow,
                ]),
            ]),
            create,
            missing,
            // The initial state is the tenant's, so this says what happens without
            // naming a state this app does not own.
            el("div", {
                class: "pqm-hint",
                text: t(
                    "mobileSheetNewTodoHint",
                    "It starts in the first state of your tenant's lifecycle. The assignee gets it in “{list}”.",
                    { list: relationListName("assignee", t("mobileViewAssignedToMe", "Assigned to me")) },
                ),
            }),
        ],
        actions.closeSheet,
    );
}

/**
 * Who you are, where you are, and the two ways out.
 *
 * ⚠ THE ONLY ROUTE TO EITHER. Until this existed the app had no sign-out control at all — the
 * `signOut()` method was written, the route was written, and nothing called it — and no way to
 * reach another installation short of clearing the browser's site data. The tenant picker existed
 * but rendered only on the signed-out branch, which a signed-in device cannot get to.
 *
 * Reached from the avatar on the home header, which is where a person looks for their account.
 */
function accountSheet(state: MobileState, actions: TodoActions): HTMLElement {
    const profile = state.profile;
    const current = activeTenant(state.knownTenants);
    const children: HTMLElement[] = [];

    if (profile) {
        children.push(
            el("div", { class: "pqm-account-who" }, [
                el("div", {
                    class: "pqm-avatar pqm-avatar-md",
                    text: initials(profile.name || "?"),
                    "aria-hidden": "true",
                }),
                el("div", { class: "pqm-account-text" }, [
                    el("div", {
                        class: "pqm-account-name",
                        text: profile.name || t("mobileSheetSignedIn", "Signed in"),
                    }),
                    el("div", { class: "pqm-account-sub", text: profile.tenantName || current?.host || "" }),
                ]),
            ]),
        );
    }

    // Every OTHER installation this device knows. The active one is named above rather than listed
    // as a choice — offering to switch to where you already are is a control that does nothing.
    const others = state.knownTenants.filter((t) => !current || t.host !== current.host);

    if (others.length > 0) {
        children.push(el("div", { class: "pqm-kicker", text: t("mobileSheetSwitchInstallation", "Switch tenant") }));
        for (const tenant of others) {
            const row = el("button", { class: "pqm-method", type: "button" }, [
                el("div", { class: "pqm-method-text" }, [
                    el("div", { class: "pqm-method-title", text: tenant.displayName || tenant.host }),
                    el("div", { class: "pqm-method-sub", text: tenantRowSubtitle(tenant) }),
                ]),
                chevronRight("var(--color-row-chevron)"),
            ]);
            bind(row, "click", () => actions.switchTenant(tenant));
            children.push(row);
        }
    }

    // ⚠ DEVICE settings, not account ones, and they sit here only because this is the app's one
    // settings screen. Stored in this browser; they apply to every tenant on it. Radio buttons, one
    // option per line: a segmented control squeezed "Match device" and "From PQFORCE" into thirds of
    // a phone's width, and these are choices to read, not to toggle.
    children.push(
        radioGroup(
            "pqm-appearance",
            t("mobileSheetAppearance", "Appearance"),
            APPEARANCES.map((appearance) => ({ value: appearance, label: appearanceLabel(appearance) })),
            state.appearance,
            (appearance) => actions.setAppearance(appearance),
        ),
    );

    // Overrides the PQFORCE account's language on this device only; "From PQFORCE" hands the
    // decision back to the account.
    children.push(
        radioGroup(
            "pqm-language",
            t("mobileSheetLanguage", "Language"),
            LANGUAGE_CHOICES.map((choice) => ({
                value: choice,
                label: languageChoiceLabel(choice),
                // Each language is named in itself, and marked so a screen reader says it right.
                lang: choice === "auto" ? undefined : choice,
            })),
            state.languageChoice,
            (choice) => actions.setLanguageChoice(choice),
        ),
    );

    const add = el("button", {
        class: "btn btn-secondary btn-block",
        type: "button",
        text: t("mobileSheetAddInstallation", "Add another tenant"),
    });
    bind(add, "click", actions.addInstallation);
    children.push(add);

    const out = el("button", { class: "btn btn-ghost btn-block pqm-account-signout", type: "button" }, [
        // Names the installation, because signing out of ONE of several is what this does — and on
        // a device paired with two, "Delete the access token" alone does not say which.
        document.createTextNode(
            current && others.length > 0
                ? t("mobileSheetSignOutOf", "Delete the access token for {host}", { host: current.host })
                : t("mobileSheetSignOut", "Delete the access token"),
        ),
    ]);
    bind(out, "click", actions.signOut);
    children.push(out);

    children.push(
        el("div", {
            class: "pqm-hint",
            // The consequence, stated: the other pairings survive, this one does not.
            text:
                others.length > 0
                    ? t(
                          "mobileSheetSignOutHintSeveral",
                          "Deleting the access token ends this tenant's access on this device. The others stay signed in.",
                      )
                    : t(
                          "mobileSheetSignOutHintOne",
                          "Deleting the access token ends this device's access. You will need to approve it again next time.",
                      ),
        }),
    );

    // "Done", not "Cancel": every control here — the appearance, the installation, signing out —
    // acts as it is touched, so there is no draft this button could discard. Same reason the
    // assignees sheet says it.
    return sheet(t("mobileSheetAccount", "Account"), children, actions.closeSheet, false, "Done");
}

/**
 * Rename a TODO and rewrite its description. Owner only — the button that opens it is.
 *
 * ⚠ THE DESCRIPTION IS HTML AND THIS IS A TEXTAREA. A value carrying lists, links, emphasis or
 * inline styles is shown READ-ONLY with a sentence saying where to edit it, because flattening
 * somebody's formatting to text and saving it cannot be undone — the rule is
 * `pqfMobileDescriptionIsPlain()` on the server, which decides it from the raw value and refuses
 * the write as well. Renaming stays possible either way: the title has nothing to do with it.
 *
 * As everywhere in these sheets, what is being typed lives in the fields until Save — routing a
 * keystroke through the store would repaint the sheet under the caret.
 */
function detailsSheet(todo: Todo, actions: TodoActions): HTMLElement {
    const editable = todo.descriptionEditable === true;
    // ⚠ WHAT IS TYPED SURVIVES A REPAINT, and it has to: a failed save toasts, a toast is a store
    // update, and every store update rebuilds this sheet — so without the draft the fields came
    // back holding the tenant's values and the edit that had just failed was gone, which is the one
    // moment it is the only copy. Kept out of the store for the usual reason (a keystroke written
    // there would rebuild the field under the caret) and cleared by renderSheet() when this sheet
    // is not the one open, so it opens on the tenant's values next time.
    if (!detailsDraft || detailsDraft.id !== todo.id) {
        detailsDraft = { id: todo.id, title: todo.title, description: todo.descriptionText ?? "" };
    }
    const draft = detailsDraft;
    const focused = document.activeElement;

    const title = el("input", {
        class: "input",
        style: "min-height:46px",
        type: "text",
        "aria-label": t("mobileSheetTodoTitle", "Title"),
    });
    title.value = draft.title;
    bind(title, "input", () => {
        draft.title = title.value;
    });

    const description = el("textarea", {
        class: "input",
        style: "min-height:140px",
        "aria-label": t("mobileSheetTodoDescription", "Description"),
        placeholder: t("mobileSheetDescriptionPlaceholder", "What is this about?"),
    });
    description.value = draft.description;
    description.disabled = !editable;
    bind(description, "input", () => {
        draft.description = description.value;
    });

    const save = el("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:48px",
        type: "button",
        text: t("mobileSheetSaveChanges", "Save changes"),
    });
    bind(save, "click", () => {
        save.disabled = true;
        // ⚠ The description is sent ONLY when this sheet was allowed to edit it. A key the route
        // does not receive is a field it does not touch, so a rename cannot flatten a formatted
        // description on its way past.
        actions.saveDetails(title.value.trim(), editable ? description.value : undefined);
    });

    const fields: HTMLElement[] = [
        el("div", { class: "field" }, [el("label", { text: t("mobileSheetTodoTitle", "Title") }), title]),
        el("div", { class: "field" }, [
            el("label", { text: t("mobileSheetTodoDescription", "Description") }),
            description,
        ]),
    ];

    if (!editable) {
        fields.push(
            el("div", { class: "pqm-note" }, [
                icon("warningCircle", 15, "var(--color-ink-5)"),
                el("div", {
                    class: "pqm-note-text",
                    text: t(
                        "mobileSheetDescriptionFormatted",
                        "This description was written with formatting, so it can only be changed in PQFORCE. You can still rename the TODO here.",
                    ),
                }),
            ]),
        );
    }

    fields.push(save);

    // Whichever field was being typed into gets the caret back, at the end of what is in it.
    for (const [node, was] of [
        [title, "pqm-details-title"],
        [description, "pqm-details-description"],
    ] as const) {
        node.classList.add(was);
        if (focused?.classList.contains(was)) {
            window.setTimeout(() => {
                node.focus();
                node.setSelectionRange(node.value.length, node.value.length);
            }, 0);
        }
    }

    return sheet(t("mobileSheetEditTodo", "Edit TODO"), fields, actions.closeSheet);
}

/** The sheet for the current state, or null when none is open. */
export function renderSheet(state: MobileState, actions: TodoActions): HTMLElement | null {
    // The people search is shared by the assignees and the owner sheet; it is kept while one is open.
    if (state.sheet !== "assignees" && state.sheet !== "owner") assigneeQuery = "";
    if (state.sheet !== "details") detailsDraft = null;
    const scrim = sheetFor(state, actions);
    // The sheet's identity across repaints — see `steadyOverlays()` in ui_mobile_shell.ts.
    if (scrim) scrim.dataset.overlay = `sheet:${state.sheet}`;

    return scrim;
}

function sheetFor(state: MobileState, actions: TodoActions): HTMLElement | null {
    // ⚠ BOTH LISTS: a TODO opened from "All TODOs" may be in neither of this person's own segments,
    // and its sheets must open all the same. This person's own copy first, as `store.selected()`.
    const todo =
        state.todos.find((t) => t.id === state.selectedId) ??
        state.allTodos?.find((t) => t.id === state.selectedId) ??
        null;

    switch (state.sheet) {
        case "transition":
            return todo && state.transitionDraft ? statusSheet(todo, state, actions) : null;
        case "priority":
            return todo ? prioritySheet(todo, state, actions) : null;
        case "assignees":
            return todo ? assigneesSheet(todo, state, actions) : null;
        case "owner":
            return todo ? ownerSheet(todo, state, actions) : null;
        case "details":
            return todo ? detailsSheet(todo, actions) : null;
        case "delete":
            return todo ? deleteSheet(todo, actions) : null;
        case "new":
            return newTodoSheet(state, actions);
        case "account":
            return accountSheet(state, actions);
        default:
            return null;
    }
}

/**
 * Hand the TODO over: pick ONE person, then confirm. They replace the current owner(s) — which is
 * what PQFORCE's own client does, and why this is not an add/remove list like the assignees.
 *
 * ⚠ TWO STEPS, like the status and the priority: a hand-over can cost this person their own say
 * over the TODO, so a stray tap must not do it. Confirm names the person, and the consequence is
 * stated when the person handing over is an owner.
 *
 * The candidates are whom the OWNER relation accepts (`ownerPeople`), which need not be whom the
 * assignee relation accepts.
 */
function ownerSheet(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    const self = state.profile?.resourceId ?? "";
    const currentIds = new Set(todo.owners.map((o) => o.id));
    const people = (state.createOptions?.ownerPeople ?? []).filter(
        (p) => !(currentIds.has(p.id) && currentIds.size === 1),
    );
    const picked = people.find((p) => p.id === state.ownerPick) ?? null;
    const busy = state.assigneeBusy !== null;

    const row = (person: { id: string; name: string }) => {
        const name = person.name || t("mobileSheetUnnamedResource", "Unnamed resource");
        const chosen = picked?.id === person.id;
        const button = el(
            "button",
            {
                class: "pqm-pick pqm-pick-option pqm-pick-person",
                type: "button",
                "aria-pressed": chosen ? "true" : "false",
            },
            [
                el("div", { class: "pqm-avatar pqm-avatar-md", text: initials(name), "aria-hidden": "true" }),
                el("span", { text: name }),
            ],
        );
        button.disabled = busy;
        bind(button, "click", () => actions.pickOwner(person.id));
        return button;
    };

    const confirm = el("button", {
        class: "btn btn-primary btn-block",
        type: "button",
        text: picked
            ? busy
                ? "…"
                : t("mobileSheetHandOverTo", "Hand over to {name}", { name: picked.name || "?" })
            : t("mobileSheetChooseOwner", "Choose who takes it over"),
    });
    confirm.disabled = !picked || busy;
    bind(confirm, "click", actions.handOver);

    const children: HTMLElement[] = [
        currently(el("span", { class: "pqm-change-value", text: namesWithSelf(todo.owners, self) || "—" })),
        people.length
            ? addList(people.map((p) => ({ name: p.name, row: row(p) })))
            : el("div", {
                  class: "pqm-empty",
                  text: state.createOptions
                      ? t("mobileSheetNobodyToHandOver", "There is nobody this TODO can be handed over to.")
                      : t("mobileSheetLoadingPeople", "Loading who can be assigned…"),
              }),
    ];
    if (currentIds.has(self)) {
        children.push(
            el("div", {
                class: "pqm-hint",
                text: t(
                    "mobileSheetHandOverHint",
                    "You will no longer own this TODO. What you may still change then depends on your permissions in PQFORCE.",
                ),
            }),
        );
    }
    children.push(confirm);

    return sheet(relationName("owner", t("mobileViewOwner", "Owner")), children, actions.closeSheet, true);
}

/** Deleting is asked once more, naming the TODO — it cannot be undone from the app. */
function deleteSheet(todo: Todo, actions: TodoActions): HTMLElement {
    const confirm = el("button", { class: "btn btn-primary btn-block pqm-confirm", type: "button" }, [
        el("span", { text: t("mobileSheetDeleteConfirm", "Delete TODO") }),
    ]);
    bind(confirm, "click", () => {
        confirm.disabled = true;
        actions.deleteTodo();
    });

    return sheet(
        t("mobileSheetDeleteTitle", "Delete TODO?"),
        [
            el("div", {
                class: "pqm-hint",
                text: t("mobileSheetDeleteText", "“{title}” will be deleted. This cannot be undone in the app.", {
                    title: todo.code ? `${todo.code} ${todo.title}` : todo.title,
                }),
            }),
            confirm,
        ],
        actions.closeSheet,
    );
}

/** A titled group of radio buttons, one option per line — the account sheet's device settings. */
function radioGroup<T extends string>(
    name: string,
    title: string,
    options: Array<{ value: T; label: string; lang?: string }>,
    checked: T,
    onChange: (value: T) => void,
): HTMLElement {
    return el("div", { class: "pqm-radio-group", role: "radiogroup", "aria-label": title }, [
        el("div", { class: "pqm-kicker", text: title }),
        ...options.map((option) => {
            const input = el("input", { type: "radio", name, value: option.value, checked: option.value === checked });
            bind(input, "change", () => onChange(option.value));
            return el("label", { class: "radio pqm-radio-row", lang: option.lang }, [
                input,
                el("span", { class: "dot", "aria-hidden": "true" }),
                document.createTextNode(option.label),
            ]);
        }),
    ]);
}
